import { styled } from "@linaria/react";
import React from "react";
import type { CreatableProps } from "react-select/creatable";
import CreatableSelect from "react-select/creatable";
import BaseSelect from "react-select/base";
import type { ActionMeta, GroupBase, OnChangeValue, StylesConfig } from "react-select";
import { useStateManager } from "react-select";
import { useCreatable } from "react-select/creatable";
import t from "../i18n";
import type { BaseOption } from "./useFormatOptionLabel";
import useFormatOptionLabel from "./useFormatOptionLabel";
import styles from "./styles";

const StyledSelect = styled(BaseSelect as unknown as typeof CreatableSelect)`
  width: 100%;
  position: relative;

  > div:first-of-type {
    min-height: 38px;
  }
`;

export type SelectProps<
  Option,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
> = CreatableProps<Option, IsMulti, Group> & {
  allowNew?: boolean;
  onEdit?: (option: Option, label: string) => Promise<void>;
  onDelete?: (option: Option) => Promise<void>;
  onCreate?: (value: string) => void | Promise<void>;
};

function getOptionLabelGlobal<Option extends BaseOption>(option: Option) {
  return option.label;
}

function ReactSelect<
  Option extends BaseOption,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = { options: Option[] },
>(
  { onEdit, onDelete, onCreate, allowNew, ...props }: SelectProps<Option, IsMulti, Group>,
  ref: React.Ref<BaseSelect<Option, IsMulti, Group>>,
) {
  const [loading, setLoading] = React.useState(false);
  props.allowCreateWhileLoading = props.allowCreateWhileLoading ?? false;
  props.createOptionPosition = props.createOptionPosition ?? "first";
  props.getOptionLabel = props.getOptionLabel ?? getOptionLabelGlobal;
  const { onChange } = props;
  props.onChange = React.useCallback(
    async (value: OnChangeValue<Option, IsMulti>, actionMeta: ActionMeta<Option>) => {
      if (actionMeta.action === "create-option") {
        const newValue = Array.isArray(value) ? value[value.length - 1] : value;
        if (!newValue?.value) throw new Error("Missing value in create?");
        if (onCreate) {
          setLoading(true);
          try {
            await onCreate(newValue.value);
          } finally {
            setLoading(false);
          }
        }
        if (allowNew) {
          onChange?.(value, actionMeta);
        }
      } else onChange?.(value, actionMeta);
    },
    [onCreate, onChange, allowNew],
  );
  props.placeholder = props.placeholder ?? t("placeholder.search");
  const formatCreateLabel = React.useCallback((str: string) => t("react_select.create", str), []);
  props.formatCreateLabel = props.formatCreateLabel ?? formatCreateLabel;
  const noOptionsMessage = React.useCallback(() => t("react_select.no_result"), []);
  props.noOptionsMessage = props.noOptionsMessage ?? noOptionsMessage;
  props.closeMenuOnSelect = props.closeMenuOnSelect ?? !props.isMulti;
  props.hideSelectedOptions = props.hideSelectedOptions ?? false;
  props.isClearable = props.isClearable ?? !props.isMulti;
  props.isSearchable = props.isSearchable ?? true;
  props.tabSelectsValue = props.tabSelectsValue ?? false;
  props.isOptionDisabled = props.isOptionDisabled ?? (option => Boolean(option.disabled));
  props.openMenuOnFocus = props.openMenuOnFocus ?? true;
  const formatOptionLabel = useFormatOptionLabel({
    onEdit,
    onDelete,
    getOptionLabel: props.getOptionLabel,
  });
  props.isLoading = props.isLoading ?? loading;
  props.formatOptionLabel = props.formatOptionLabel ?? formatOptionLabel;
  props.components = React.useMemo(
    () =>
      props.components ?? {
        IndicatorSeparator: null,
      },
    [props.components],
  );
  props.styles = props.styles ?? (styles as unknown as StylesConfig<Option, IsMulti, Group>);

  // all props must be set before being massaged by ReactSelect hooks
  const stateManagedProps = useStateManager(props);
  const creatableProps = useCreatable(stateManagedProps);
  props = allowNew || onCreate ? creatableProps : stateManagedProps;

  return <StyledSelect ref={ref} {...props} />;
}

// https://stackoverflow.com/a/58473012
const ForwardedReactSelect = React.memo(React.forwardRef(ReactSelect)) as <
  Option extends BaseOption,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = { options: Option[] },
>(
  p: SelectProps<Option, IsMulti, Group> & { ref?: React.Ref<HTMLDivElement> },
) => React.ReactElement;

export default ForwardedReactSelect;
